import { Badge, Box, Heading, Text, useBreakpointValue } from "@chakra-ui/react-v3";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { Fragment } from "react";
import { HighlightedText } from "../../components/highlighted-text";
import { NoteSnippetsDrawer } from "../../components/note-snippets-drawer";
import { TableFilterPopover } from "../../components/table-filter-popover";
import { useTableFilters } from "../../hooks/use-table-filters";
import { TableRow } from "../../utils/tables-metadata";
import { ImagingReportRowData } from "./imaging-reports-rows";
import { hasImagingNoteMatches, useImagingFilterOptions } from "./imaging-shared";

export type SearchImagingReportsTableProps = {
  rows: TableRow[];
  onRowClick: (row: TableRow) => void;
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
  displayName: string;
  onPdfView?: (docId: string) => void;
  isRowViewed?: (row: TableRow) => boolean;
};

/**
 * Imaging reports table component for search mode.
 * Renders card-style layout with expandable note snippets.
 * Parent provides the card wrapper - this component renders content only.
 */
export function SearchImagingReportsTable({
  rows,
  onRowClick,
  filterQuery,
  displayName,
}: SearchImagingReportsTableProps) {
  const filterOptions = useImagingFilterOptions(rows);
  const isSmallScreen = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false;

  const { activeFilters, setActiveFilters, filteredRows } = useTableFilters(rows, filterOptions);

  function handleRowClick(row: ImagingReportRowData) {
    onRowClick(row);
  }

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="md" fontWeight="semibold" color="fg">
          {displayName} ({filteredRows.length})
        </Heading>
        {filterOptions.length > 0 && (
          <TableFilterPopover
            filters={filterOptions}
            activeFilters={activeFilters}
            setActiveFilters={newFilters => setActiveFilters(newFilters)}
          />
        )}
      </Box>

      <Box display="flex" flexDirection="column" gap={3}>
        {filteredRows.map(row => {
          const imagingRow = row as unknown as ImagingReportRowData;
          const hasMatches = hasImagingNoteMatches(row, filterQuery);

          return (
            <Fragment key={row.id}>
              <Box
                borderRadius="md"
                borderWidth="1px"
                borderColor="border"
                boxShadow="sm"
                overflow="hidden"
                bg="bg.canvas"
              >
                {/* Main row - clickable */}
                <Box
                  cursor="pointer"
                  onClick={() => handleRowClick(imagingRow)}
                  _hover={{ bg: "interactive.hover" }}
                  p={4}
                >
                  <Box
                    display="grid"
                    gridTemplateColumns={{ base: "1fr 1fr", md: "repeat(5, 1fr)" }}
                    gap={4}
                  >
                    <Box>
                      {hasMatches ? (
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Text textStyle="body.md" fontWeight="semibold" color="fg">
                            <HighlightedText
                              text={imagingRow.imagingDisplay}
                              searchQuery={filterQuery}
                            />
                          </Text>
                          <Badge
                            size="xs"
                            p={0}
                            bg="transparent"
                            display="inline-flex"
                            alignItems="center"
                            gap={1}
                            w="fit-content"
                            fontSize="2xs"
                            opacity={0.7}
                          >
                            Note excerpts below
                          </Badge>
                        </Box>
                      ) : (
                        <Text textStyle="body.sm" color="fg">
                          <HighlightedText
                            text={imagingRow.imagingDisplay}
                            searchQuery={filterQuery}
                          />
                        </Text>
                      )}
                    </Box>
                    {!isSmallScreen && (
                      <>
                        <Box>
                          <Text textStyle="body.sm" color="fg">
                            <HighlightedText
                              text={imagingRow.imagingModality}
                              searchQuery={filterQuery}
                            />
                          </Text>
                        </Box>
                        <Box>
                          <Text textStyle="body.sm" color="fg">
                            <HighlightedText
                              text={imagingRow.bodyRegion}
                              searchQuery={filterQuery}
                            />
                          </Text>
                        </Box>
                        <Box>
                          <Text textStyle="body.sm" color="fg">
                            <HighlightedText text={imagingRow.location} searchQuery={filterQuery} />
                          </Text>
                        </Box>
                      </>
                    )}
                    <Box>
                      <Text textStyle="body.sm" color="fg">
                        <HighlightedText text={imagingRow.date} searchQuery={filterQuery} />
                      </Text>
                    </Box>
                  </Box>
                </Box>

                {/* Note snippets drawer */}
                {hasMatches && (
                  <NoteSnippetsDrawer
                    noteData={row.noteData as { fullText: string } | undefined}
                    filterQuery={filterQuery}
                    rowId={row.id}
                    onSnippetClick={() => handleRowClick(imagingRow)}
                  />
                )}
              </Box>
            </Fragment>
          );
        })}
      </Box>
    </>
  );
}
